import {FC, memo} from 'react'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Switch} from '@/components/commons/switch/Switch'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {TimeSlot} from '@/features/users-availability/components/time-slot/TimeSlot'
import {DayOfWeek, dayOfWeekTranslationKeys} from '@/features/users-availability/types'
import {useTranslation} from '@/translations/i18n'
import {Control, Controller, FieldArrayWithId} from 'react-hook-form'
import {AvailabilityFormValidationSchema} from '@/features/users-availability/utils'
import {Text} from '@/components/ui/text/Text'

// Day switch row component
export const DaySwitchRow: FC<{
    day: DayOfWeek
    control: Control<AvailabilityFormValidationSchema>
}> = ({day, control}) => {
    const {t} = useTranslation()

    return (
        <Flexbox align="center" gap={2}>
            <Controller
                control={control}
                name={`${day}.enabled`}
                render={({field}) => (
                    <Flexbox align="center" gap={3}>
                        <Switch onChange={field.onChange} checked={field.value} />
                        <Text size="textMd" weight="500">
                            {t(dayOfWeekTranslationKeys[day])}
                        </Text>
                    </Flexbox>
                )}
            />
        </Flexbox>
    )
}
DaySwitchRow.displayName = 'DaySwitchRow'

// Time slots list component
export const TimeSlotsList: FC<{
    fields: FieldArrayWithId[]
    dayName: DayOfWeek
    onRemoveSlot: (index: number) => void
    error?: string
}> = memo(({fields, dayName, onRemoveSlot, error}) => {
    // Show remove button only if we have more than one time slot
    const hasMultipleSlots = fields.length > 1

    return (
        <>
            {fields.map((field, index) => (
                <TimeSlot
                    key={field.id}
                    dayName={dayName}
                    index={index}
                    onRemove={() => onRemoveSlot(index)}
                    showRemoveButton={hasMultipleSlots}
                />
            ))}

            {error && <InputHelpText error={error} />}
        </>
    )
})
TimeSlotsList.displayName = 'TimeSlotsList'
